import { useMemo, useState, type KeyboardEvent } from 'react';
import { Link } from 'react-router-dom';
import {
    BUILDING_NAMES,
    DEFAULT_SCIENCES,
    SHIP_BUILD_REFUND_MAX,
    SHIP_BUILD_REFUND_MIN,
    SHIP_DEFINITIONS,
    TEAM_PLANET_SHIP_BUILD_LANES,
    getShipDefinition,
    type Planet,
    type PlayerRace,
    type TickCheckpointEventOf,
} from '@kaeliux/shared';
import { calculateTeamPlanetFreePopulation, extractBuildingLevels, extractScienceLevels, resolveTeamPlanetShipUpgrades } from '@kaeliux/game-engine';
import { useAuthStore } from '../store/authStore';
import { useUniverseStore } from '../store/universeStore';
import { useConfirmDialog } from '../contexts/ConfirmDialogContext';
import { useTeamPlanetSession } from '../contexts/TeamPlanetContext';
import { usePlanetView } from '../contexts/PlanetViewContext';
import { useNow } from '../hooks/useNow';
import { useStoreTeamPlanet } from '../hooks/useTeamPlanet';
import { cancelTeamPlanetShipBuild, queueTeamPlanetShips } from '../services/teamPlanet.service';
import ShipCard, { SHIP_CATEGORY_ORDER, SHIP_CATEGORY_TITLES } from '../components/ShipCard';
import QueueItemCard from '../components/QueueItemCard';
import { Panel } from '../components/ui/Panel';
import { SelectableCard } from '../components/ui/SelectableCard';
import { linkButtonClasses } from '../components/ui/buttonStyles';
import { zoneAccentStyle } from '../components/planet/zoneAccent';
import { getBuildingIcon } from '../utils/buildingIcons';
import { getShipIcon } from '../utils/shipIcons';
import { eventProgress } from '../utils/eventProgress';
import { formatCountdown } from '../utils/formatters';
import { RACE_LABELS } from '../utils/teamPlanetStyle';
import { cn } from '../utils/cn';

const REFUND_RANGE_LABEL = `${SHIP_BUILD_REFUND_MIN * 100}-${SHIP_BUILD_REFUND_MAX * 100}%`;

interface QueuedTeamShipsProps {
    race: PlayerRace;
    event: TickCheckpointEventOf<'shipBuild'>;
    now: number;
    canCancel: boolean;
    onUpdated: (planet: Planet) => Promise<void>;
}

/** A queued ship batch in one race's lane. No Rush on the Team planet. */
function QueuedTeamShips({ race, event, now, canCancel, onUpdated }: QueuedTeamShipsProps) {
    const { openDialog } = useConfirmDialog();
    const [isCancelling, setIsCancelling] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const def = getShipDefinition(race, event.eventDetails.shipType);
    const startMs = event.eventDetails.startDate.getTime();
    const endMs = event.endDate.getTime();
    const remainingMs = endMs - now;

    async function doCancel() {
        setIsCancelling(true);
        setError(null);
        try {
            await onUpdated(await cancelTeamPlanetShipBuild(event.eventId));
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Cancel failed');
        } finally {
            setIsCancelling(false);
        }
    }

    return (
        <QueueItemCard
            image={(
                <div className="w-full h-full rounded-tile border border-hairline bg-surface-void/60 flex items-center justify-center overflow-hidden">
                    <img src={getShipIcon(race, def.id)} alt="" className="w-full h-full object-contain" />
                </div>
            )}
            overlayLabel={`× ${event.eventDetails.quantity}`}
            name={def.name}
            statusText={remainingMs > 0 ? `${startMs <= now ? 'Building…' : 'Queued'} ${formatCountdown(remainingMs)}` : 'Finishing…'}
            progress={eventProgress(startMs, endMs, now)}
            error={error}
            isCancelling={isCancelling}
            imageBoxClassName="w-20 aspect-[4/3]"
            onCancel={canCancel ? () => openDialog({
                title: `Cancel ${def.name} construction?`,
                message: `Finished ships stay docked; the unbuilt remainder is refunded at ${REFUND_RANGE_LABEL} of its cost.`,
                confirmLabel: 'Cancel construction',
                danger: true,
                onConfirm: () => { doCancel().catch(() => { }); },
            }) : undefined}
        />
    );
}

/**
 * The Team planet's three shipyards, one per race, sharing one Factory
 * building level but queuing in independent lanes. Every member builds in
 * their own race's shipyard; the others are shown so the alliance can see
 * what is being built, and the leader may cancel any batch.
 */
export default function TeamPlanetShipyardPage() {
    const { teamPlanet, isLeader } = useTeamPlanetSession();
    const view = usePlanetView();
    const store = useStoreTeamPlanet();
    const { profile } = useAuthStore();
    const { speedMultiplier } = useUniverseStore();
    const now = useNow();
    const viewerRace = profile?.race;
    const [activeRace, setActiveRace] = useState<PlayerRace>(viewerRace ?? 'humans');

    const buildingLevels = useMemo(() => extractBuildingLevels(teamPlanet.buildings), [teamPlanet.buildings]);
    const scienceLevels = useMemo(
        () => extractScienceLevels(teamPlanet.sciences ?? DEFAULT_SCIENCES),
        [teamPlanet.sciences],
    );
    const shipQueue = useMemo(
        () => (teamPlanet.tickCheckpoints ?? []).filter(
            (e): e is TickCheckpointEventOf<'shipBuild'> => e.eventType === 'shipBuild' && !e.cancelled,
        ),
        [teamPlanet.tickCheckpoints],
    );
    const shipyardLevel = buildingLevels.shipyard ?? 0;
    const upgradeLevels = useMemo(() => resolveTeamPlanetShipUpgrades(teamPlanet, activeRace), [teamPlanet, activeRace]);
    const laneEvents = shipQueue.filter((e) => e.eventDetails.queueLane === activeRace);
    const isOwnLane = activeRace === viewerRace;
    const planetForRace = useMemo(
        () => ({ ...teamPlanet, ships: teamPlanet.teamShips?.[activeRace]?.docked ?? {} }),
        [teamPlanet, activeRace],
    );
    const freePopulation = calculateTeamPlanetFreePopulation(teamPlanet, teamPlanet.tickCheckpoints ?? []);

    function onTabKeyDown(event: KeyboardEvent<HTMLDivElement>) {
        const index = TEAM_PLANET_SHIP_BUILD_LANES.indexOf(activeRace);
        const step = event.key === 'ArrowRight' ? 1 : event.key === 'ArrowLeft' ? -1 : 0;
        if (!step) return;
        event.preventDefault();
        const next = TEAM_PLANET_SHIP_BUILD_LANES[(index + step + TEAM_PLANET_SHIP_BUILD_LANES.length) % TEAM_PLANET_SHIP_BUILD_LANES.length] ?? activeRace;
        setActiveRace(next);
        document.getElementById(`shipyard-tab-${next}`)?.focus();
    }

    return (
        <main className="flex-1 w-full max-w-7xl mx-auto px-4 py-4 flex flex-col gap-4" style={zoneAccentStyle('military')} data-testid="team-planet-shipyard-page">
            <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                    <h1 className="text-display text-content-primary">{BUILDING_NAMES.shipyard}</h1>
                    <p className="text-body text-content-muted">Three race lanes share one {BUILDING_NAMES.shipyard}. Build in your own race&apos;s lane.</p>
                </div>
                <Link to="/team-planet" className={linkButtonClasses('secondary')}>← Back to Team Planet</Link>
            </div>

            {shipyardLevel === 0 ? (
                <Panel variant="solid" padding="md" className="flex flex-col gap-2" data-testid="team-planet-no-shipyard">
                    <h2 className="text-title text-content-primary">No {BUILDING_NAMES.shipyard} yet</h2>
                    <p className="text-body text-content-muted">Construct the {BUILDING_NAMES.shipyard} on the Team planet to open all three race lanes.</p>
                    <Link to={view.zoneRoute('military', 'shipyard')} className={cn(linkButtonClasses('secondary', 'sm'), 'self-start')}>
                        Go to the Military zone
                    </Link>
                </Panel>
            ) : (
                <>
                    <div role="tablist" aria-label="Race lanes" className="grid grid-cols-3 gap-2" onKeyDown={onTabKeyDown}>
                        {TEAM_PLANET_SHIP_BUILD_LANES.map((race) => {
                            const selected = race === activeRace;
                            const queued = shipQueue.filter((e) => e.eventDetails.queueLane === race).length;
                            return (
                                <SelectableCard
                                    key={race}
                                    id={`shipyard-tab-${race}`}
                                    selected={selected}
                                    role="tab"
                                    aria-selected={selected}
                                    aria-controls="shipyard-tabpanel"
                                    tabIndex={selected ? 0 : -1}
                                    data-testid={`shipyard-tab-${race}`}
                                    onClick={() => setActiveRace(race)}
                                >
                                    <img
                                        src={getBuildingIcon('shipyard', shipyardLevel, race)}
                                        alt=""
                                        className={cn('h-14 w-11 shrink-0 rounded-tile border border-hairline bg-space-950 object-cover', !selected && 'opacity-70')}
                                    />
                                    <span className="flex min-w-0 flex-col">
                                        <span className="text-title text-content-primary truncate">{RACE_LABELS[race]}</span>
                                        <span className="text-micro text-content-muted tabular-nums">
                                            {race === viewerRace ? 'Your yard · ' : ''}{queued} queued
                                        </span>
                                    </span>
                                </SelectableCard>
                            );
                        })}
                    </div>

                    <div id="shipyard-tabpanel" role="tabpanel" aria-labelledby={`shipyard-tab-${activeRace}`} className="flex flex-col gap-4">
                        <div className="flex flex-wrap items-center justify-between gap-2 text-body text-content-muted">
                            <span>
                                {isOwnLane
                                    ? `Your ${RACE_LABELS[activeRace]} ships are built here and dock on the Team planet.`
                                    : `Only ${RACE_LABELS[activeRace]} members can queue ships in this yard.`}
                            </span>
                            <span className="tabular-nums">
                                Free population: {freePopulation.toLocaleString()} / {teamPlanet.resources.population.capacity.toLocaleString()}
                            </span>
                        </div>

                        {laneEvents.length > 0 && (
                            <Panel variant="solid" data-testid={`team-planet-lane-${activeRace}`}>
                                <h2 className="text-title text-content-secondary mb-1">{RACE_LABELS[activeRace]} Queue</h2>
                                <div className="flex flex-wrap gap-3">
                                    {laneEvents.map((event) => (
                                        <QueuedTeamShips
                                            key={event.eventId}
                                            race={activeRace}
                                            event={event}
                                            now={now}
                                            canCancel={isLeader || event.eventDetails.queuedByUserId === profile?.id}
                                            onUpdated={store}
                                        />
                                    ))}
                                </div>
                            </Panel>
                        )}

                        {SHIP_CATEGORY_ORDER.map((category) => (
                            <section key={category} className="flex flex-col gap-3">
                                <h2 className="text-heading text-content-secondary">{SHIP_CATEGORY_TITLES[category]}</h2>
                                {Object.values(SHIP_DEFINITIONS[activeRace])
                                    .filter((def) => def.category === category)
                                    .map((def) => (
                                        <ShipCard
                                            key={`${activeRace}-${def.id}`}
                                            def={def}
                                            planet={planetForRace}
                                            race={activeRace}
                                            scienceLevels={scienceLevels}
                                            buildingLevels={buildingLevels}
                                            freePopulation={freePopulation}
                                            speedMultiplier={speedMultiplier}
                                            upgradeLevel={upgradeLevels[def.id] ?? 0}
                                            upgradeLinked={isOwnLane}
                                            readOnlyReason={isOwnLane ? null : `${RACE_LABELS[activeRace]} members build this ship`}
                                            onBuild={async (quantity) => store(await queueTeamPlanetShips(def.id, quantity))}
                                        />
                                    ))}
                            </section>
                        ))}
                    </div>
                </>
            )}
        </main>
    );
}
